/* ==========================================================================
   Sanctus Servo — Cart Goal Ladder engine
   --------------------------------------------------------------------------
   Vanilla JS, no libraries. Reads window.SanctusCartGoal (set by
   snippets/sanctus-cart-goal-head.liquid from theme settings) and powers the
   tiered free-shipping / discount ladder on four surfaces at once: the cart
   drawer, the cart page, the announcement bar, and the product page.

   ARCHITECTURE
     Liquid server-renders the correct state at page load. From then on ONE
     module-level coordinator owns the truth: it subscribes once to
     cartUpdate, resolves the real cart, and broadcasts to every
     <sanctus-cart-goal> on the page. Elements never subscribe to cartUpdate
     themselves and never render on connect — see RULE below.

   RULE: connectedCallback NEVER renders.
     Server HTML is authoritative at element birth; the coordinator is
     authoritative afterwards. Dawn replaces .drawer__inner wholesale on a
     drawer quantity change, so an element that rendered from a stale cache
     on connect would visibly regress the bar while the /cart.js fetch for
     the add-to-cart path is still in flight.

   PROGRESS BASIS: items_subtotal_price, never total_price.
     total_price is net of the order discount, so crossing $75 would apply
     the $10 and knock the bar BACKWARDS to $65. items_subtotal_price is
     also what Shopify evaluates discount minimums against, so the bar and
     the real discount cross at the same instant.

   Companion files: snippets/sanctus-cart-goal-head.liquid (config),
   snippets/sanctus-cart-goal.liquid (markup), assets/sanctus-cart-goal.css.
   See Documentation/06-cart-goal-ladder.md.
   ========================================================================== */

(function () {
  'use strict';

  var CFG = window.SanctusCartGoal;
  if (!CFG || !CFG.enabled) return;

  var RUNGS = (CFG.rungs || [])
    .filter(function (r) {
      return r && r.at > 0 && (r.freeShipping || r.value > 0);
    })
    .sort(function (a, b) {
      return a.at - b.at;
    });
  if (!RUNGS.length) return;

  var MAX_AT = RUNGS[RUNGS.length - 1].at;
  var REDUCED = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ------------------------------------------------------------------ */
  /* 1. MONEY                                                            */
  /* ------------------------------------------------------------------ */
  /* Only the moving remainder (gap, savings) is formatted here. Every
     fixed amount and every product price is pre-formatted by Liquid, so
     those are always right in any currency without this code running. */

  var PLACEHOLDER = /\{\{\s*(\w+)\s*\}\}/;

  function group(cents, precision, thousands, decimal) {
    if (isNaN(cents) || cents == null) cents = 0;
    var n = (cents / 100).toFixed(precision);
    var parts = n.split('.');
    var whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
    return whole + (parts[1] ? decimal + parts[1] : '');
  }

  function money(cents) {
    var f = CFG.moneyFormat || '${{amount}}';
    var m = f.match(PLACEHOLDER);
    if (!m) return f;
    var value;
    switch (m[1]) {
      case 'amount_no_decimals':
        value = group(cents, 0, ',', '.');
        break;
      case 'amount_with_comma_separator':
        value = group(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = group(cents, 0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        value = group(cents, 2, "'", '.');
        break;
      default:
        value = group(cents, 2, ',', '.');
    }
    return f.replace(PLACEHOLDER, value);
  }

  /* ------------------------------------------------------------------ */
  /* 2. MODEL                                                            */
  /* ------------------------------------------------------------------ */
  /* Discounts DO NOT stack — Shopify applies the better one. bestOff is a
     MAX, never a sum, and `upgrade` is the delta on the reward, which is
     what the copy interpolates. Free shipping, by contrast, is cumulative:
     once its rung is earned it is kept at every rung above. Rungs arrive
     sorted and merged (snippets/sanctus-cart-goal-rungs.liquid), so the
     free-shipping rung can sit anywhere on the ladder. */

  /* Cents a rung's discount is worth at a given subtotal. A percent rung
     grows with the cart, so "better" is always judged at a subtotal. */
  function worth(rung, subtotal) {
    if (!rung || !(rung.value > 0)) return 0;
    if (rung.kind === 'percent') return Math.round((subtotal * rung.value) / 100);
    return rung.value;
  }

  function percent(subtotal, tier) {
    if (MAX_AT <= 0) return 0;
    if (CFG.barScale === 'equal') {
      if (tier >= RUNGS.length) return 100;
      var seg = 100 / RUNGS.length;
      var floorAt = tier === 0 ? 0 : RUNGS[tier - 1].at;
      var ceilAt = RUNGS[tier].at;
      var within = ceilAt > floorAt ? (subtotal - floorAt) / (ceilAt - floorAt) : 0;
      return clamp(tier * seg + within * seg);
    }
    return clamp((subtotal / MAX_AT) * 100);
  }

  function clamp(n) {
    return Math.max(0, Math.min(100, n));
  }

  function evaluate(subtotal) {
    var achieved = [];
    var bestOff = 0;
    var best = null;
    var freeShip = false;
    for (var i = 0; i < RUNGS.length; i++) {
      if (subtotal >= RUNGS[i].at) {
        achieved.push(RUNGS[i]);
        var w = worth(RUNGS[i], subtotal);
        if (w > bestOff) {
          bestOff = w;
          best = RUNGS[i];
        }
        if (RUNGS[i].freeShipping) freeShip = true;
      }
    }
    var tier = achieved.length;
    var next = RUNGS[tier] || null;
    return {
      subtotal: subtotal,
      tier: tier,
      /* The rung whose DISCOUNT applies — not necessarily the highest one
         reached, which may be the shipping-only rung. */
      best: best,
      bestOff: bestOff,
      freeShip: freeShip,
      next: next,
      gap: next ? next.at - subtotal : 0,
      pct: percent(subtotal, tier)
    };
  }

  /* ------------------------------------------------------------------ */
  /* 3. COPY                                                             */
  /* ------------------------------------------------------------------ */
  /* SYNC NOTE: these four join rules are mirrored in Liquid in
     snippets/sanctus-cart-goal.liquid so the ladder still reads correctly
     with JS disabled. The wording fragments themselves (reward,
     offText, atText) come from the config and are the single
     source of truth — only the joins are duplicated.

     Guard rail: never say "more" in a tier-1 headline and never state a
     reward TOTAL when the shopper already holds a lower reward. State the
     upgrade delta, or the $20 reads as additive on top of the $10. */

  /* Empty when no discount is held — the headline already says free
     shipping is unlocked, and repeating it reads as filler. */
  function maxReward(s) {
    if (s.freeShip && s.bestOff > 0) return 'Free shipping + ' + s.best.offText + ' off at checkout.';
    if (s.bestOff > 0) return s.best.offText + ' off at checkout.';
    return '';
  }

  /* What reaching `rung` would ADD for a shopper in state `s`. Free
     shipping only counts if not already held (and the item ships); the
     discount only counts if it beats the one already held — judged at the
     rung's subtotal, where Shopify would compare them — and is then
     phrased as an upgrade so it never reads as additive. */
  function gain(s, rung, ships) {
    var g = { ship: false, off: false, upgrade: false, offText: '' };
    if (!rung) return g;
    g.ship = !!(rung.freeShipping && !s.freeShip && ships !== false);
    var w = worth(rung, rung.at);
    if (w > 0 && w > worth(s.best, rung.at)) {
      g.off = true;
      g.upgrade = s.bestOff > 0;
      g.offText = rung.offText;
    }
    return g;
  }

  function gainLabel(g) {
    if (g.ship && g.off) return g.upgrade ? 'FREE shipping + an upgrade to ' + g.offText + ' off' : 'FREE shipping + ' + g.offText + ' off';
    if (g.ship) return 'FREE shipping';
    if (g.off) return g.upgrade ? 'an upgrade to ' + g.offText + ' off' : g.offText + ' off';
    return '';
  }

  /* Names the REWARD the shopper gets for adding one, not the fact that the
     gap closes — "for FREE shipping" beats "and you're there". */
  function fillerHeading(s, mode) {
    if (!s.next) return '';
    var label = gainLabel(gain(s, s.next)) || s.next.reward;
    /* Always name the reward. "Closes the gap" was jargon — shoppers read
       "gap" as something about the product, not about their basket total. */
    if (mode !== 'unlock') return 'Add one of these to get closer to ' + label + '.';
    return 'Add one of these for ' + label + '.';
  }

  /* Mirrors the sitewide-sale maths in snippets/price.liquid, so a variant
     price coming from variantChange lands on the same figure Liquid would
     have rendered. Rounds the same way (nearest cent). */
  function salePrice(cents, pct) {
    if (!pct || pct <= 0 || !cents) return cents || 0;
    return Math.round((cents * (100 - pct)) / 100);
  }

  /* Lower-case, no trailing clause — for use mid-sentence on the product
     page ("Your cart already has free shipping + $20 off."). */
  function heldReward(s) {
    if (s.freeShip && s.bestOff > 0) return 'free shipping + ' + s.best.offText + ' off';
    if (s.freeShip) return 'free shipping';
    if (s.bestOff > 0) return s.best.offText + ' off';
    return 'every reward unlocked';
  }

  function unlockedLine(s) {
    if (s.freeShip && s.bestOff > 0) return 'Free shipping unlocked — and ' + s.best.offText + ' off applied.';
    if (s.freeShip) return 'Free shipping unlocked.';
    return s.best.offText + ' off applied.';
  }

  function buildCopy(s) {
    var c = { headline: '', sub: '' };

    if (s.tier === 0 && s.next) {
      c.headline = 'You’re ' + money(s.gap) + ' away from ' + s.next.reward + '.';
      if (s.next.double) c.sub = 'Two rewards unlock together at ' + s.next.atText + '.';
    } else if (s.next) {
      c.headline = unlockedLine(s);
      var g = gain(s, s.next);
      if (g.off && g.upgrade && !g.ship) {
        c.sub = 'Add ' + money(s.gap) + ' to upgrade your ' + s.best.offText + ' off to ' + g.offText + ' off.';
      } else {
        c.sub = 'Add ' + money(s.gap) + ' for ' + (gainLabel(g) || s.next.reward) + '.';
      }
    } else {
      /* Only claim discounts when there are some. With a shipping-only
         ladder "All discounts applied" would announce something the
         shopper is not getting. */
      c.headline = s.bestOff > 0 ? 'All discounts applied.' : 'Free shipping unlocked.';
      c.sub = maxReward(s);
    }
    return c;
  }

  function valueText(s) {
    var parts = [];
    if (s.next) parts.push(money(s.subtotal) + ' of ' + s.next.atText + ' toward ' + s.next.reward);
    else parts.push('All rewards unlocked');
    return parts.join('');
  }

  /* Savings receipt. Prefers the discount Shopify has ACTUALLY applied;
     falls back to the ladder's prediction when the automatic discounts only
     evaluate at checkout. cart.total_discount is deliberately unused — it
     includes line-level discounts already baked into the progress basis,
     so it would double-count. */
  function savingsText(cart, s) {
    var apps = cart.cart_level_discount_applications || [];
    var real = 0;
    for (var i = 0; i < apps.length; i++) real += apps[i].total_allocated_amount || 0;
    if (!real) {
      var diff = (cart.items_subtotal_price || 0) - (cart.total_price || 0);
      if (diff > 0) real = diff;
    }

    var src = CFG.savingsSource || 'auto';
    var amount = 0;
    var atCheckout = false;

    if (src === 'real_only') {
      amount = real;
    } else if (src === 'predicted_only') {
      amount = s.bestOff;
      atCheckout = real === 0 && amount > 0;
    } else if (real > 0) {
      amount = real;
    } else {
      amount = s.bestOff;
      atCheckout = amount > 0;
    }

    /* Shipping is never given a number by default: the real cost is unknown
       until checkout, so any figure would be invented. cart_goal_shipping_value
       is an opt-in escape hatch and is labelled "typical". */
    var ship = '';
    if (s.freeShip) {
      ship = CFG.shippingValue > 0 ? ' and shipping is on us (a typical ' + CFG.shippingValueText + ' value).' : ' and shipping is on us.';
    }

    if (amount > 0) {
      return 'You’re saving ' + money(amount) + (atCheckout ? ' at checkout' : '') + (ship ? ' —' + ship : '.');
    }
    if (s.freeShip) return 'Shipping is on us.';
    return '';
  }

  /* ------------------------------------------------------------------ */
  /* 4. COORDINATOR                                                      */
  /* ------------------------------------------------------------------ */
  /* One subscription for the page lifetime. Never holds element
     references — broadcast() re-queries, so elements destroyed and rebuilt
     by Dawn's section replacement are picked up automatically. */

  var cart = CFG.cart || { item_count: 0, items_subtotal_price: 0, total_price: 0 };
  var token = 0;
  var inflight = null;
  var lastAnnouncedTier = null;

  /* Tier memory lives HERE, not on the element. Dawn destroys and rebuilds
     <sanctus-cart-goal> on every cart change, and the replacement HTML is
     rendered server-side against the NEW cart — so a fresh element's
     data-tier is already the post-change value and it has no way to know a
     rung was just crossed. The coordinator is the only thing that survives
     the swap, so it owns the before/after comparison. */
  var lastTier = null;

  /* Multi-currency guard. Shopify converts discount minimums per market by
     its own rules, so a threshold we converted ourselves would not match
     what the shopper actually gets. Hide rather than mislead; the
     announcement bar falls back to the merchant's static text for free. */
  function currencyMismatch(c) {
    return !!(c && c.currency && CFG.currency && c.currency !== CFG.currency);
  }

  function resolveCart(payload) {
    /* Fast path: a quantity change publishes the full parsed cart. */
    if (payload && payload.cartData && typeof payload.cartData.items_subtotal_price === 'number') {
      return Promise.resolve(payload.cartData);
    }
    /* Slow path: product-form.js publishes the /cart/add.js response, which
       is a LINE ITEM with no cart totals on it. Dedupe concurrent callers. */
    if (inflight) return inflight;
    inflight = fetch(CFG.cartUrl + '.js', { headers: { Accept: 'application/json' } })
      .then(function (r) {
        return r.json();
      })
      .then(
        function (c) {
          inflight = null;
          return c;
        },
        function (e) {
          inflight = null;
          throw e;
        }
      );
    return inflight;
  }

  function broadcast(crossed) {
    var nodes = document.querySelectorAll('sanctus-cart-goal');
    for (var i = 0; i < nodes.length; i++) {
      if (typeof nodes[i].update === 'function') nodes[i].update(cart, crossed);
    }
    syncAnnouncementSlider();
  }

  /* Dawn's slider measures slide widths on init. If the announcement
     slideshow was display:none at load and the cart is then emptied, those
     widths are 0 — re-measure on reveal. */
  function syncAnnouncementSlider() {
    var goal = document.querySelector('sanctus-cart-goal[data-goal-variant="announcement"]');
    if (!goal || !goal.hidden) return;
    var slider = document.querySelector('slideshow-component.announcement-bar');
    if (!slider) return;
    if (typeof slider.initPages === 'function') {
      try {
        slider.initPages();
      } catch (e) {
        /* older Dawn without initPages — the resize below still helps */
      }
    }
    window.dispatchEvent(new Event('resize'));
  }

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, function (payload) {
      var mine = ++token;
      return resolveCart(payload)
        .then(function (c) {
          if (mine !== token) return; /* a newer change already landed */

          /* Compare against the tier we last broadcast, seeded at boot from
             the page-load cart, so the very first add still celebrates. */
          var before = lastTier === null ? evaluate(cart.items_subtotal_price || 0).tier : lastTier;
          var after = evaluate(c.items_subtotal_price || 0).tier;
          lastTier = after;

          cart = c;
          CFG.cart = c;
          broadcast(after > before);
        })
        .catch(function () {
          /* leave the last good state on screen rather than blanking it */
        });
    });
  }

  /* Theme editor: a section re-render replaces server HTML that was built
     against the cart at page load, so re-apply the live state. */
  document.addEventListener('shopify:section:load', broadcast);

  /* ------------------------------------------------------------------ */
  /* 5. GAP FILLER                                                       */
  /* ------------------------------------------------------------------ */

  var POOL = (CFG.filler || []).filter(function (p) {
    return p && p.vid && p.price > 0;
  });

  /* Reads product_id / variant_id explicitly. A /cart.js line item's `id` is
     the VARIANT id, so a bare `id` lookup would silently match the wrong
     thing; the Liquid seed uses the same two field names for this reason. */
  function cartKeys(c) {
    var vids = {};
    var pids = {};
    var items = c.items || [];
    for (var i = 0; i < items.length; i++) {
      vids[items[i].variant_id] = true;
      pids[items[i].product_id] = true;
    }
    return { vids: vids, pids: pids };
  }

  /* Deterministic tie-break by product id is NOT optional: without it the
     cards reshuffle on every quantity tick and the upsell moves out from
     under the shopper's cursor. */
  function selectFillers(gap, c) {
    var keys = cartKeys(c);
    var count = CFG.fillerCount || 3;
    var maxOver = CFG.fillerMaxOvershoot || 0;

    var candidates = POOL.filter(function (p) {
      return !keys.vids[p.vid] && !keys.pids[p.id];
    });

    var closers = candidates
      .filter(function (p) {
        return p.price >= gap;
      })
      .sort(function (a, b) {
        return a.price - gap - (b.price - gap) || a.id - b.id;
      });

    var partials = candidates
      .filter(function (p) {
        return p.price < gap;
      })
      .sort(function (a, b) {
        return b.price - a.price || a.id - b.id;
      });

    var tight = closers.filter(function (p) {
      return p.price - gap <= maxOver;
    });
    var loose = closers.filter(function (p) {
      return p.price - gap > maxOver;
    });

    var picked = tight.slice(0, count);
    if (picked.length < count) picked = picked.concat(partials.slice(0, count - picked.length));
    if (picked.length < count) picked = picked.concat(loose.slice(0, count - picked.length));

    return {
      items: picked,
      /* Honest copy: never imply a one-click unlock that will not happen. */
      mode: tight.length > 0 ? 'unlock' : partials.length > 0 ? 'closer' : 'unlock'
    };
  }

  function postConfig(body) {
    return {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
      body: JSON.stringify(body)
    };
  }

  /* Branch on SURFACE, not on whether a <cart-drawer> exists: theme.liquid
     renders the drawer on EVERY template including /cart, so an
     existence check would pop the drawer open on top of the cart page. */
  function addToCart(vid, button, host) {
    var onCartPage = !!document.getElementById('main-cart-items');
    var mainItems = document.getElementById('main-cart-items');
    var mainFooter = document.getElementById('main-cart-footer');
    var sections = onCartPage
      ? [mainItems.dataset.id, mainFooter ? mainFooter.dataset.id : null, 'cart-icon-bubble', 'cart-live-region-text'].filter(Boolean)
      : ['cart-drawer', 'cart-icon-bubble'];

    button.setAttribute('aria-disabled', 'true');
    button.classList.add('is-loading');

    return fetch(CFG.cartAddUrl + '.js', postConfig({
      items: [{ id: vid, quantity: 1 }],
      sections: sections,
      sections_url: window.location.pathname
    }))
      .then(function (r) {
        return r.json();
      })
      .then(function (res) {
        if (res.status) {
          /* 422: sold out between page load and click. */
          showError(host, res.description || res.message || 'Sorry, that is no longer available.');
          dropFromPool(vid);
          return;
        }

        if (onCartPage) {
          patchCartPage(res);
        } else {
          var drawer = document.querySelector('cart-drawer');
          if (drawer && typeof drawer.renderContents === 'function') drawer.renderContents(res);
        }

        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'sanctus-cart-goal',
            productVariantId: vid,
            cartData: res
          });
        }

        /* The button that was clicked is destroyed by the re-render, so
           move focus deliberately instead of dropping it on <body>. */
        focusLadder();
      })
      .catch(function () {
        showError(host, 'Something went wrong. Please try again.');
      })
      .finally(function () {
        button.removeAttribute('aria-disabled');
        button.classList.remove('is-loading');
      });
  }

  /* Also closes a stock-Dawn gap: CartItems.onCartUpdate only refetches
     main-cart-items and never refreshes #main-cart-footer, so any
     non-"cart-items" add on /cart leaves the totals stale. */
  function patchCartPage(res) {
    if (!res.sections) return;
    [
      { id: 'main-cart-items', selector: '.js-contents' },
      { id: 'main-cart-footer', selector: '.js-contents' }
    ].forEach(function (target) {
      var el = document.getElementById(target.id);
      if (!el) return;
      var html = res.sections[el.dataset.id];
      if (!html) return;
      var parsed = new DOMParser().parseFromString(html, 'text/html').querySelector(target.selector);
      var dest = el.querySelector(target.selector);
      if (parsed && dest) dest.innerHTML = parsed.innerHTML;
    });

    var bubble = document.getElementById('cart-icon-bubble');
    if (bubble && res.sections['cart-icon-bubble']) {
      var b = new DOMParser().parseFromString(res.sections['cart-icon-bubble'], 'text/html').querySelector('.shopify-section');
      if (b) bubble.innerHTML = b.innerHTML;
    }
  }

  function dropFromPool(vid) {
    for (var i = POOL.length - 1; i >= 0; i--) {
      if (POOL[i].vid === vid) POOL.splice(i, 1);
    }
  }

  function showError(host, message) {
    var slot = host && host.querySelector('[data-goal-slot="error"]');
    if (!slot) return;
    slot.textContent = message;
    slot.hidden = false;
  }

  /* Gold confetti burst on a tier crossing. Purely decorative: spans are
     aria-hidden, pointer-events:none, and are removed when the animation
     ends, so nothing is left in the DOM or the accessibility tree. Built
     here rather than in CSS because each piece needs its own random vector. */
  var CONFETTI_PIECES = 22;

  function burstConfetti(host) {
    var layer = host.querySelector('[data-goal-confetti]');
    if (!layer) return;
    layer.textContent = '';

    for (var i = 0; i < CONFETTI_PIECES; i++) {
      var piece = document.createElement('span');
      piece.className = 'sanctus-goal__confetti-piece';
      /* Fan upward and outward from the bar, with enough spread that the
         burst reads as an explosion rather than a fountain. */
      var angle = -90 + (Math.random() * 150 - 75);
      var distance = 40 + Math.random() * 70;
      var rad = (angle * Math.PI) / 180;
      piece.style.setProperty('--cx', (Math.cos(rad) * distance).toFixed(1) + 'px');
      piece.style.setProperty('--cy', (Math.sin(rad) * distance).toFixed(1) + 'px');
      piece.style.setProperty('--rot', Math.round(Math.random() * 720 - 360) + 'deg');
      piece.style.setProperty('--delay', (Math.random() * 0.18).toFixed(2) + 's');
      piece.style.setProperty('--scale', (0.6 + Math.random() * 0.7).toFixed(2));
      piece.style.left = (12 + Math.random() * 76).toFixed(1) + '%';
      /* Even thirds across the three golds. (An earlier %3 / %5 pair gave
         some pieces both classes and skewed the mix.) */
      var kind = i % 3;
      if (kind === 1) piece.classList.add('is-soft');
      else if (kind === 2) piece.classList.add('is-deep');
      layer.appendChild(piece);
    }

    /* One timer for the whole burst rather than a listener per piece. */
    clearTimeout(host._confettiTimer);
    host._confettiTimer = setTimeout(function () {
      layer.textContent = '';
    }, 1800);
  }

  function focusLadder() {
    var head = document.querySelector('sanctus-cart-goal[data-goal-variant="drawer"] [data-goal-slot="headline"]');
    if (!head) head = document.querySelector('sanctus-cart-goal[data-goal-variant="cart"] [data-goal-slot="headline"]');
    if (head) {
      head.setAttribute('tabindex', '-1');
      head.focus();
    }
  }

  /* ------------------------------------------------------------------ */
  /* 6. ELEMENT                                                          */
  /* ------------------------------------------------------------------ */

  var CELEBRATE_MS = 2200;

  class SanctusCartGoal extends HTMLElement {
    connectedCallback() {
      this.variantName = this.dataset.goalVariant || 'drawer';
      this.onClick = this.handleClick.bind(this);
      this.addEventListener('click', this.onClick);
      if (this.variantName === 'product') this.bindProduct();
      /* NO render here — see RULE at the top of this file. */
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      if (this._unsubVariant) this._unsubVariant();
      if (this._qtyEl && this._onQty) this._qtyEl.removeEventListener('change', this._onQty);
      if (this._celebrateTimer) clearTimeout(this._celebrateTimer);
    }

    /* -- product surface ------------------------------------------- */

    bindProduct() {
      var self = this;
      /* Liquid already applied the sitewide sale to data-variant-price, but
         variantChange hands back the LIST price, so keep the percentage
         around to re-apply it ourselves. */
      this._salePct = parseInt(this.dataset.salePct, 10) || 0;
      this._variantPrice = parseInt(this.dataset.variantPrice, 10) || 0;
      this._available = this.dataset.variantAvailable !== 'false';
      this._requiresShipping = this.dataset.requiresShipping !== 'false';

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this._unsubVariant = subscribe(PUB_SUB_EVENTS.variantChange, function (event) {
          if (!event || !event.data) return;
          /* Without this filter a quick-add modal's variant picker would
             drive the underlying page's ladder. */
          if (String(event.data.sectionId) !== String(self.dataset.sectionId)) return;
          var v = event.data.variant;
          self._available = !!(v && v.available);
          self._variantPrice = v ? salePrice(v.price, self._salePct) : 0;
          /* Shippability is a VARIANT property, and can differ between
             variants of one product (a digital option alongside a physical). */
          if (v && typeof v.requires_shipping === 'boolean') self._requiresShipping = v.requires_shipping;
          self.update(cart);
        });
      }

      this._qtyEl = document.getElementById('Quantity-' + this.dataset.sectionId);
      if (this._qtyEl) {
        var timer;
        this._onQty = function () {
          clearTimeout(timer);
          timer = setTimeout(function () {
            self.update(cart);
          }, 300);
        };
        this._qtyEl.addEventListener('change', this._onQty);
      }
    }

    quantity() {
      var q = this._qtyEl ? parseInt(this._qtyEl.value, 10) : 1;
      return q > 0 ? q : 1;
    }

    /* -- interaction ----------------------------------------------- */

    handleClick(event) {
      var button = event.target.closest('[data-goal-add]');
      if (!button) return;
      event.preventDefault();
      var vid = parseInt(button.getAttribute('data-goal-add'), 10);
      if (!vid || button.getAttribute('aria-disabled') === 'true') return;
      addToCart(vid, button, this);
    }

    /* -- render ----------------------------------------------------- */

    update(c, crossed) {
      if (!c) return;

      if (currencyMismatch(c)) {
        this.hidden = true;
        return;
      }

      var subtotal = c.items_subtotal_price || 0;
      var empty = !c.item_count;
      var s = evaluate(subtotal);

      if (this.variantName === 'product') return this.renderProduct(c, s);
      if (this.variantName === 'savings') return this.renderSavings(c, s, empty);
      if (this.variantName === 'filler') return this.renderFiller(c, s, empty);
      if (this.variantName === 'announcement') return this.renderAnnouncement(c, s, empty);
      return this.renderLadder(c, s, empty, crossed);
    }

    renderLadder(c, s, empty, crossed) {
      this.hidden = empty;
      if (empty) return;

      var copy = buildCopy(s);
      this.setSlot('headline', copy.headline);
      this.setSlot('sub', copy.sub);
      this.celebrate(crossed);
      this.setBar(s);

      /* Exactly one live region page-wide (drawer only) and only on a tier
         CHANGE — announcing every cent would fight Dawn's own
         #CartDrawer-LiveRegionText and be unbearable. */
      if (this.variantName === 'drawer') {
        if (lastAnnouncedTier !== null && s.tier !== lastAnnouncedTier) {
          this.setSlot('announce', copy.headline + ' ' + copy.sub);
        }
        lastAnnouncedTier = s.tier;
      }
    }

    renderAnnouncement(c, s, empty) {
      this.hidden = empty;
      if (empty) return;
      var copy = buildCopy(s);
      this.setSlot('headline', copy.sub ? copy.headline + ' ' + copy.sub : copy.headline);
    }

    renderSavings(c, s, empty) {
      var text = empty ? '' : savingsText(c, s);
      this.hidden = !text;
      if (text) this.setSlot('savings', text);
    }

    renderFiller(c, s, empty) {
      if (empty || !s.next || !CFG.fillerEnabled || !POOL.length) {
        this.hidden = true;
        this._fillerKey = null;
        return;
      }

      /* Stability: re-select only when the tier changed, one of the shown
         products entered the cart, or the block is coming back from hidden.
         Re-selecting on every subtotal change would shuffle the cards out
         from under the shopper's cursor mid-interaction. */
      var keys = cartKeys(c);
      var shown = this._shownVids || [];
      var invalidated = shown.some(function (v) {
        return keys.vids[v];
      });

      if (this._fillerKey === s.tier && !invalidated && !this.hidden) return;

      var result = selectFillers(s.gap, c);
      if (!result.items.length) {
        this.hidden = true;
        this._fillerKey = null;
        return;
      }

      this.hidden = false;
      this._fillerKey = s.tier;
      this._shownVids = result.items.map(function (p) {
        return p.vid;
      });

      this.setSlot('filler-heading', fillerHeading(s, result.mode));
      this.paintCards(result.items);

      var err = this.querySelector('[data-goal-slot="error"]');
      if (err) err.hidden = true;
    }

    renderProduct(c, s) {
      if (!this._available || !this._variantPrice) {
        this.hidden = true;
        return;
      }
      if (s.tier >= RUNGS.length && CFG.productHideWhenMaxed) {
        this.hidden = true;
        return;
      }

      var hypo = s.subtotal + this._variantPrice * this.quantity();
      var after = evaluate(hypo);
      var ships = this._requiresShipping;
      var line = '';

      /* What this item would unlock, judged on the WHOLE state before and
         after — it can jump several rungs at once. Shipping is only
         promised for an item that ships (not a gift card). */
      if (after.tier > s.tier) {
        var newShip = after.freeShip && !s.freeShip && ships;
        var newOff = after.bestOff > s.bestOff;
        if (newOff && s.bestOff > 0 && !newShip) {
          line = 'Adding this upgrades you to ' + after.best.offText + ' off.';
        } else if (newShip || newOff) {
          line =
            'Adding this unlocks ' +
            gainLabel({ ship: newShip, off: newOff, upgrade: newOff && s.bestOff > 0, offText: newOff ? after.best.offText : '' }) +
            '.';
        }
        /* Then point at the next rung, so the shopper always sees the next
           step rather than a dead end. */
        if (line && after.next) {
          var then = gainLabel(gain(after, after.next, ships));
          if (then) line += ' Then ' + money(after.gap) + ' more for ' + then + '.';
        }
      }

      if (!line && after.next) {
        /* Name the reward — "$32.00 from the discount" told the shopper
           nothing about what the discount actually is. */
        var pending = gainLabel(gain(after, after.next, ships));
        if (!pending) {
          this.hidden = true;
          return;
        }
        line = 'Adding this puts you ' + money(after.gap) + ' away from ' + pending + '.';
      } else if (!line) {
        line = 'Your cart already has ' + heldReward(s) + '.';
      }

      this.hidden = false;
      this.setSlot('headline', line);
      this.setBar(s);
      this.style.setProperty('--goal-ghost-pct', after.pct.toFixed(2) + '%');
    }

    /* -- primitives -------------------------------------------------- */

    setSlot(name, text) {
      var el = this.querySelector('[data-goal-slot="' + name + '"]');
      if (!el) return;
      el.textContent = text || '';
      if (name === 'sub' || name === 'savings') el.hidden = !text;
    }

    setBar(s) {
      this.style.setProperty('--goal-pct', s.pct.toFixed(2) + '%');
      this.dataset.tier = String(s.tier);

      var track = this.querySelector('[data-goal-slot="track"]');
      if (track) {
        track.setAttribute('aria-valuenow', String(Math.round(s.pct)));
        track.setAttribute('aria-valuetext', valueText(s));
      }

      var nodes = this.querySelectorAll('[data-goal-node]');
      for (var i = 0; i < nodes.length; i++) {
        var idx = parseInt(nodes[i].getAttribute('data-goal-node'), 10);
        nodes[i].dataset.state = s.tier > idx ? 'unlocked' : 'locked';
      }
    }

    /* `crossed` is decided by the coordinator, which is the only thing that
       outlives Dawn's DOM replacement. A page merely LOADED at $80 never
       celebrates, because no cartUpdate crossed a rung. */
    celebrate(crossed) {
      if (!crossed || !CFG.celebrate || REDUCED) return;

      var self = this;
      /* Restart the animation cleanly if two rungs fall in quick succession:
         removing and re-adding in the same frame is a no-op, so force reflow. */
      this.classList.remove('is-celebrating');
      void this.offsetWidth;
      this.classList.add('is-celebrating');
      burstConfetti(this);

      clearTimeout(this._celebrateTimer);
      this._celebrateTimer = setTimeout(function () {
        self.classList.remove('is-celebrating');
      }, CELEBRATE_MS);
    }

    paintCards(items) {
      var grid = this.querySelector('[data-goal-slot="filler-grid"]');
      var tpl = this.querySelector('template[data-goal-card]');
      if (!grid || !tpl) return;

      grid.textContent = '';
      items.forEach(function (p) {
        var node = tpl.content.firstElementChild.cloneNode(true);

        var img = node.querySelector('[data-goal-card-image]');
        if (img) {
          if (p.img) {
            img.src = p.img;
            if (p.img2x) img.srcset = p.img + ' 1x, ' + p.img2x + ' 2x';
            img.alt = p.alt || p.title;
          } else {
            img.remove();
          }
        }

        var title = node.querySelector('[data-goal-card-title]');
        if (title) {
          title.textContent = p.title;
          title.href = p.url;
        }

        var price = node.querySelector('[data-goal-card-price]');
        if (price) price.textContent = p.priceText;

        var button = node.querySelector('[data-goal-card-button]');
        if (button) {
          if (p.multi) {
            /* Options cannot be chosen in one click — send them to the PDP
               rather than adding a variant they did not pick. */
            var link = document.createElement('a');
            link.className = button.className;
            link.href = p.url;
            link.textContent = 'Choose options';
            button.replaceWith(link);
          } else {
            button.setAttribute('data-goal-add', String(p.vid));
            button.setAttribute('aria-label', 'Add ' + p.title + ' to cart, ' + p.priceText);
          }
        }

        grid.appendChild(node);
      });
    }
  }

  if (!customElements.get('sanctus-cart-goal')) {
    customElements.define('sanctus-cart-goal', SanctusCartGoal);
  }
})();
